import { Fraction } from 'bi-fraction';
import { utils } from 'zksync-ethers';
import { estimateDefaultBridgeDepositL2Gas } from 'zksync-ethers/build-esm/utils';
import { BridgeMiddleware as BridgeMiddlewareAbi, ERC20 as ERC20Abi, Zkcro as ZkcroAbi, ZkCroMintAndBridge as ZkCroMintAndBridgeAbi, } from '../abis';
import { CRO_DECIMALS_ON_ETHEREUM } from '../config/constants';
import { ChainLayer, FromBalanceTooLowError as FromBalanceTooLowError, } from '../types';
import { executeTx, getContract, getProvider, } from '../utils';
export class ZkCroMintAndBridge {
    constructor(options) {
        this.options = options;
        this.getL2GasLimit = async (amountOfZkCroToBridge) => estimateDefaultBridgeDepositL2Gas(getProvider(this.options.l1ProviderOptions, ChainLayer.L1), 
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        getProvider(this.options.l2ProviderOptions, ChainLayer.L2), this.options.contractAddressMap.ZkCRO, amountOfZkCroToBridge, this.options.depositorAddress);
        this.zkCroMintAndBridgeContract = this.createZkCroMintAndBridgeContract();
        this.zkCroContract = this.createZkCroContract();
        this.wcroContract = this.createWcroContract();
        this.middleware = this.createMiddlewareContract();
    }
    /**
     * Deposits the given amount of tokens to L2 via the YbTokenBridge (YbEthBridge or YbUsdBridge) contract.
     * Will execute approval if needed.
     *
     * @param croAmount cro amount to be deposited
     * @returns receipt of the deposit transaction
     * @throws if the CRO balance is too low
     */
    async depositToken(croAmount) {
        const croAmountBigInt = croAmount.shl(CRO_DECIMALS_ON_ETHEREUM).quotient;
        const amountOfZkCroToBridge = await this.zkCroContract.convertToShare(croAmountBigInt);
        const [{ l2GasFee, l2GasLimit }, croBalance] = await Promise.all([
            this.getL2GasFee(croAmount),
            this.wcroContract.balanceOf(this.options.depositorAddress),
        ]);
        const totalCroNeeded = croAmountBigInt + l2GasFee;
        if (totalCroNeeded > croBalance) {
            throw new FromBalanceTooLowError(new Fraction(totalCroNeeded - croBalance).shr(CRO_DECIMALS_ON_ETHEREUM));
        }
        await this.approveDepositTokenIfNecessary(totalCroNeeded);
        const tx = this.zkCroMintAndBridgeContract.mintAndBridge(this.options.depositorAddress, amountOfZkCroToBridge, l2GasLimit, totalCroNeeded);
        return await executeTx(tx);
    }
    async getL2GasFee(croAmount) {
        const croAmountBigInt = croAmount.shl(CRO_DECIMALS_ON_ETHEREUM).quotient;
        const amountOfZkCroToBridge = await this.zkCroContract.convertToShare(croAmountBigInt);
        const [l2GasLimit, l1GasPrice] = await Promise.all([
            this.getL2GasLimit(amountOfZkCroToBridge).then(BigInt),
            getProvider(this.options.l1ProviderOptions, ChainLayer.L1)
                .getFeeData()
                .then((feeData) => feeData.maxFeePerGas),
        ]);
        const l2GasPerPubdataByteLimit = utils.REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT;
        const l2TxCostInZkCRO = await this.middleware.l2TransactionBaseCost(l1GasPrice ?? 0n, l2GasLimit, l2GasPerPubdataByteLimit);
        const feeInCro = await this.zkCroContract.convertToAsset(l2TxCostInZkCRO);
        // (from article) This value is the sum of cro which the user wanna bridge, plus the gas fee in zkcro, and
        // a little buffer due do conversion error ... so for best experience, use 1 wei at least. 10 wei also safe.
        const l2GasFee = feeInCro + 10n;
        return {
            l2GasFee,
            l2GasLimit,
        };
    }
    async approveDepositTokenIfNecessary(amount) {
        const allowance = await this.wcroContract.allowance(this.options.depositorAddress, this.options.contractAddressMap.ZkCroMintAndBridge);
        if (allowance >= amount)
            return;
        const approveTx = this.wcroContract.approve(this.options.contractAddressMap.ZkCroMintAndBridge, amount);
        await executeTx(approveTx);
    }
    createZkCroMintAndBridgeContract() {
        return getContract(this.options.contractAddressMap.ZkCroMintAndBridge, ZkCroMintAndBridgeAbi, this.options.signerOrProvider);
    }
    createWcroContract() {
        return getContract(this.options.contractAddressMap.WCRO, ERC20Abi, this.options.signerOrProvider);
    }
    createZkCroContract() {
        return getContract(this.options.contractAddressMap.ZkCRO, ZkcroAbi, this.options.signerOrProvider);
    }
    createMiddlewareContract() {
        return getContract(this.options.contractAddressMap.BridgeMiddleware, BridgeMiddlewareAbi, this.options.signerOrProvider);
    }
}
